import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Text,
  Button,
  LoadingOverlay,
  Title,
  Paper,
  Group,
} from '@mantine/core';
import { IconPlus, IconDashboard } from '@tabler/icons-react';
import { AuditCard } from './components/AuditCard';
import { AuditFilters } from './components/AuditFilters';
import { useAudits } from './hooks/useAudits';
import { AuditWithDetails } from './types';
import { ReportService } from '../Berichte/service';
import { pb } from '../../lib/pocketbase';

export function Dashboard() {
  const navigate = useNavigate();
  const { audits, loading, error, deleteAudit } = useAudits();
  const [searchTerm, setSearchTerm] = useState('');

  const handleEditAudit = (audit: AuditWithDetails) => {
    navigate(`/audit/${audit.auditnummer}`);
  };

  const handleNewAudit = () => {
    navigate('/audit/new');
  };

  const handleShowReport = async (audit: AuditWithDetails) => {
    try {
      await ReportService.generateReport(audit.id, {
        showIstzustand: true,
        showMassnahme: true,
        showBemerkung: false,
        showAnhange: true,
        showDatum: true,
        showAuditoren: true,
        showDienststelle: true,
        showAdresse: true,
        showSicherheitssysteme: true,
        showBegleitpersonen: true,
        orientation: 'landscape',
        standardText: "Standardtext für den Bericht."
      });
    } catch (error) {
      console.error('Error generating report:', error);
    }
  };

  const handleDeleteAudit = async (audit: AuditWithDetails) => {
    if (window.confirm('Sind Sie sicher, dass Sie dieses Audit löschen möchten?')) {
      await deleteAudit(audit.id);
    }
  };

  const handleDuplicateAudit = async (audit: AuditWithDetails) => {
    try {
      // Generate new audit number
      const maxAuditNumber = Math.max(...audits.map(a => parseInt(a.auditnummer) || 0), 0);
      const newAuditNumber = (maxAuditNumber + 1).toString();

      // Create new audit with copied data
      const newAudit = await pb.collection('audits').create({
        auditnummer: newAuditNumber,
        auditdatum: new Date().toISOString().split('T')[0], // Today's date
        dienststelle: audit.dienststelle,
        bereich: audit.bereich || null,
        auditor: audit.auditor,
        kontrollpunkt: audit.kontrollpunkt,
        begleitpersonen: audit.begleitpersonen || '',
        bemerkungen: audit.bemerkungen || ''
      });

      // Duplicate all massnahmen
      if (audit.massnahmen && audit.massnahmen.length > 0) {
        // Get the highest nummer from all massnahmen to generate unique numbers
        const allMassnahmen = await pb.collection('ist_zustand_massnahmen').getFullList({
          sort: '-nummer',
          fields: 'nummer'
        });
        const maxNummer = allMassnahmen.length > 0 ? allMassnahmen[0].nummer : 0;
        
        for (let i = 0; i < audit.massnahmen.length; i++) {
          const massnahme = audit.massnahmen[i];
          const massnahmeData = {
            audit: newAudit.id,
            reihenfolge: i + 1,
            nummer: maxNummer + i + 1, // Generate unique nummer
            istzustand: massnahme.istzustand || '',
            massnahme: massnahme.massnahme || '',
            bereich: massnahme.bereich || '',
            erledigt: false,
            bemerkung: massnahme.bemerkung || ''
          };
          await pb.collection('ist_zustand_massnahmen').create(massnahmeData);
        }
      }

      // Navigate to edit the new audit
      navigate(`/audit/${newAuditNumber}`);
    } catch (error) {
      console.error('Error duplicating audit:', error);
      alert('Fehler beim Duplizieren des Audits');
    }
  };

  const filteredAudits = audits.filter(audit => {
    const dienststelleName = audit.expand?.dienststelle?.name || '';
    return dienststelleName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <Container size="xl" py="xl">
      <Paper shadow="xs" p="md" withBorder mb="xl">
        <Group justify="space-between" mb="lg">
          <Group gap="xs">
            <IconDashboard size={24} style={{ color: 'var(--mantine-color-blue-6)' }} />
            <Title order={2}>Audit-Übersicht</Title>
            <Text c="dimmed" fw={500}>({filteredAudits.length})</Text>
          </Group>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleNewAudit}
          >
            Neues Audit
          </Button>
        </Group>

        <AuditFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
        />

        <Grid>
          <LoadingOverlay visible={loading} />
          {error && (
            <Grid.Col>
              <Text color="red" ta="center" mt="xl">
                {error}
              </Text>
            </Grid.Col>
          )}
          {!loading && !error && filteredAudits.length === 0 && (
            <Grid.Col>
              <Text ta="center" c="dimmed" mt="xl">
                Keine Audits gefunden
              </Text>
            </Grid.Col>
          )}
          {filteredAudits.map((audit) => (
            <Grid.Col key={audit.id} span={{ base: 12, sm: 6, lg: 4 }}>
              <AuditCard
                audit={audit}
                onEdit={handleEditAudit}
                onDelete={handleDeleteAudit}
                onShowReport={handleShowReport}
                onDuplicate={handleDuplicateAudit}
              />
            </Grid.Col>
          ))}
        </Grid>
      </Paper>
    </Container>
  );
}
